import React, {useState} from "react";
import {Button, Col, Form, Input, Modal, Row, Select} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useForm} from "antd/es/form/Form";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {
    HealthCheckerGroup,
    HealthCheckerNotificationMethod,
} from "../../../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../../../services/health-checker/health-checker.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {UserSelect} from "../../../../users/common/user-select/user-select.component";
import {
    healthCheckerGroupFormToRequest,
    healthCheckerGroupToFormValues,
} from "./check-groups.helpers";
import {HealthCheckerGroupFormValues} from "./check-groups.types";

interface HealthCheckerGroupModalProps {
    group: HealthCheckerGroup | null;
    onCancel: () => void;
    onComplete: () => Promise<void> | void;
}

const notificationMethods: HealthCheckerNotificationMethod[] = ["none", "telegram", "email"];

export const HealthCheckerGroupModal: React.FC<HealthCheckerGroupModalProps> = ({
    group,
    onCancel,
    onComplete,
}) => {
    const t = useTranslate();
    const [form] = useForm<HealthCheckerGroupFormValues>();
    const [loading, setLoading] = useState<boolean>(false);

    const submit = async (values: HealthCheckerGroupFormValues) => {
        const request = healthCheckerGroupFormToRequest(values);
        const memberIds = request.members.map((member) => member.user);
        let completed = false;

        if (new Set(memberIds).size !== memberIds.length) {
            form.setFields([{
                name: ["members"],
                errors: [t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.DUPLICATE-MEMBERS", {
                    defaultValue: "The same user cannot be added more than once.",
                })],
            }]);
            return;
        }

        try {
            setLoading(true);
            if (group) {
                await healthCheckerService.updateGroup(group.id, request);
            } else {
                await healthCheckerService.createGroup(request);
            }
            await onComplete();
            notifications.successfully();
            completed = true;
        } catch (error: any) {
            form.setFields(error.response?.data?.fields || []);
        } finally {
            setLoading(false);
        }

        if (completed) {
            onCancel();
        }
    };

    return (
        <Modal
            className="health-center-check-groups-modal"
            title={t(
                group
                    ? "HEALTH-CENTER.SETTINGS.CHECK-GROUPS.EDIT"
                    : "HEALTH-CENTER.SETTINGS.CHECK-GROUPS.CREATE",
                {defaultValue: group ? "Edit check group" : "Create check group"}
            )}
            width={760}
            open
            destroyOnClose
            confirmLoading={loading}
            cancelButtonProps={{disabled: loading}}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form<HealthCheckerGroupFormValues>
                form={form}
                layout="vertical"
                autoComplete="off"
                initialValues={healthCheckerGroupToFormValues(group)}
                onFinish={submit}
            >
                <Form.Item
                    label={t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.NAME", {defaultValue: "Group name"})}
                    name="name"
                    rules={[{
                        required: true,
                        whitespace: true,
                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE", {defaultValue: "Please enter a value"}),
                    }]}
                >
                    <Input disabled={loading} />
                </Form.Item>

                <Form.List
                    name="members"
                    rules={[{
                        validator: async (_, members) => {
                            if (!members || members.length === 0) {
                                throw new Error(t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.MEMBER-REQUIRED", {
                                    defaultValue: "Add at least one group member.",
                                }));
                            }
                        },
                    }]}
                >
                    {(fields, {add, remove}, {errors}) => (
                        <>
                            {fields.map((field) => (
                                <Row key={field.key} gutter={12} align="middle" className="health-center-check-groups-modal__member-row">
                                    <Col xs={24} md={14}>
                                        <Form.Item
                                            label={t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.USER", {defaultValue: "User"})}
                                            name={[field.name, "user"]}
                                            rules={[{
                                                required: true,
                                                message: t("FROM.ERROR.PLEASE_SELECT_VALUE", {defaultValue: "Please select a value"}),
                                            }]}
                                        >
                                            <UserSelect disabled={loading} labelInValue />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={20} md={8}>
                                        <Form.Item
                                            label={t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.NOTIFICATION-METHOD", {
                                                defaultValue: "Notification method",
                                            })}
                                            name={[field.name, "notificationMethod"]}
                                            rules={[{required: true}]}
                                        >
                                            <Select disabled={loading}>
                                                {notificationMethods.map((method) => (
                                                    <Select.Option key={method} value={method}>
                                                        {t(`HEALTH-CENTER.SETTINGS.CHECK-GROUPS.NOTIFICATION.${method.toUpperCase()}`, {
                                                            defaultValue: method.charAt(0).toUpperCase() + method.slice(1),
                                                        })}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={4} md={2}>
                                        <Button
                                            className="health-center-check-groups-modal__remove-member"
                                            danger
                                            disabled={loading}
                                            onClick={() => remove(field.name)}
                                            icon={<FontAwesomeIcon icon={["fas", "times"]} />}
                                        />
                                    </Col>
                                </Row>
                            ))}

                            <Form.ErrorList errors={errors} />
                            <Button
                                type="dashed"
                                block
                                disabled={loading}
                                onClick={() => add({notificationMethod: "none"})}
                                icon={<FontAwesomeIcon icon={["fas", "plus"]} />}
                            >
                                {t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.ADD-MEMBER", {defaultValue: "Add member"})}
                            </Button>
                        </>
                    )}
                </Form.List>
            </Form>
        </Modal>
    );
};
